import React, {useState} from 'react';
import {useI18n} from '@udemy-v2/i18n';
import {
    FooterInDemandCareesLinks,
    FooterCertificationBySkillLinks,
    FooterWebDevelopmentLinks,
    FooterITCertificationLinks,
    FooterLeadershipLinks,
    FooterDataScienceLinks,
    FooterCommunicationLinks,
    FooterBusinessAnalyticsLinks,
} from './footer.common';
import styles from './footer.module.scss';
import {Icon} from '@udemy-v2/icons';

interface AccordionSection {
    id: string;
    title: string;
    content: React.ReactNode;
}

/**
 * Mobile accordion for skills and certifications in the footer.
 * Only one section can be open at a time.
 */
export function FooterSkillsAccordion() {
    const {gettext} = useI18n();
    const [openSections, setOpenSections] = useState<Set<string>>(new Set());

    const sections: AccordionSection[] = [
        {
            id: 'in-demand-careers',
            title: gettext('In-demand Careers'),
            content: <FooterInDemandCareesLinks />,
        },
        {
            id: 'certifications-by-skill',
            title: gettext('Certifications by Skill'),
            content: <FooterCertificationBySkillLinks />,
        },
        {
            id: 'web-development',
            title: gettext('Web Development'),
            content: <FooterWebDevelopmentLinks />,
        },
        {
            id: 'it-certifications',
            title: gettext('IT Certifications'),
            content: <FooterITCertificationLinks />,
        },
        {
            id: 'leadership',
            title: gettext('Leadership'),
            content: <FooterLeadershipLinks />,
        },
        {
            id: 'data-science',
            title: gettext('Data Science'),
            content: <FooterDataScienceLinks />,
        },
        {
            id: 'communication',
            title: gettext('Communication'),
            content: <FooterCommunicationLinks />,
        },
        {
            id: 'business-analytics',
            title: gettext('Business Analytics & Intelligence'),
            content: <FooterBusinessAnalyticsLinks />,
        },
    ];

    const handleToggle = (id: string) => {
        setOpenSections((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    return (
        <section
            aria-labelledby="skills-certifications-title"
            className={styles['footer-skills-accordion']}
        >
            <h2
                id="skills-certifications-title"
                className={styles['footer-skills-accordion-title']}
            >
                {gettext('Explore top skills and certifications')}
            </h2>
            <ul className={styles['footer-skills-accordion-list']}>
                {sections.map((section) => (
                    <li key={section.id} className={styles['footer-skills-accordion-section']}>
                        <button
                            className={styles['footer-skills-accordion-toggle']}
                            aria-expanded={openSections.has(section.id)}
                            aria-controls={`section-panel-${section.id}`}
                            id={`section-header-${section.id}`}
                            onClick={() => handleToggle(section.id)}
                        >
                            <span>{section.title}</span>
                            <span
                                aria-hidden="true"
                                className={
                                    openSections.has(section.id)
                                        ? styles['chevron-open']
                                        : styles['chevron-closed']
                                }
                            >
                                <Icon glyph="expand" label="Expand section" />
                            </span>
                        </button>
                        <div
                            id={`section-panel-${section.id}`}
                            role="region"
                            aria-labelledby={`section-header-${section.id}`}
                            hidden={!openSections.has(section.id)}
                            className={styles['footer-skills-accordion-panel']}
                        >
                            {openSections.has(section.id) && section.content}
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}
